/**
 * Normalize strategy-sheet / brand API errors for the dashboard and results UI.
 * Hides legacy internal phrasing if an older API response slips through.
 */
import { getActiveEntityBrand } from './activeEntity';

const LEGACY_JARGON_RE = /postgres|snapshot|vector sheet sync/i;

const fallbackStrategyUnavailable = () => {
  const brand = getActiveEntityBrand()?.displayName;
  const support = brand || 'your brand';
  return "This strategy's data is not available in RiskLo. "
    + `It may have been removed or renamed. Try again later, or contact ${support} support.`;
};

const fallbackCurrentResultsUnavailable = () => {
  const brand = getActiveEntityBrand()?.displayName;
  const support = brand || 'your brand';
  return 'Current Results data is not available in RiskLo '
    + `right now. Try again later, or contact ${support} support.`;
};

/**
 * Last-resort denial copy. The server sends a message naming the active brand,
 * so this is only reached when the payload carries no message at all.
 */
export function membershipDeniedFallbackMessage() {
  const brand = getActiveEntityBrand()?.displayName;
  if (brand) {
    return `Access denied. You must have an active ${brand} membership to access this data.`;
  }
  return 'Access denied. You do not have access to this brand. An active membership or admin grant is required.';
}

/**
 * True when an API response indicates a missing membership for the active
 * brand.
 *
 * `requiresMembership` is the authoritative signal and is set by the server on
 * every denial. The text check is only a fallback for older responses — it must
 * NOT look for the word "Vector", since the message now names whichever brand
 * the user is in.
 */
export function isMembershipAccessDenied(response, data) {
  if (response?.status === 403 && data?.requiresMembership) return true;
  const msg = typeof data?.error === 'string' ? data.error : '';
  return response?.status === 403 && /\bmembership\b/i.test(msg);
}

/**
 * User-facing copy for a membership-denied payload.
 *
 * Prefer the server's message verbatim: it already names the active brand and,
 * when one is configured, that brand's checkout URL. Substituting our own text
 * here is what used to tell Kairos users to go buy a Vector membership.
 */
export function membershipDeniedMessage(data) {
  const msg = typeof data?.error === 'string' ? data.error.trim() : '';
  return msg || membershipDeniedFallbackMessage();
}

/** True when a message is a membership denial, whichever brand it names. */
export function isMembershipDenialText(message) {
  const raw = String(message || '');
  return /\bmembership\b/i.test(raw) && /access denied/i.test(raw);
}

export function userFacingStrategyFetchError(message) {
  const raw = String(message || '').trim();
  if (!raw) return fallbackStrategyUnavailable();
  if (LEGACY_JARGON_RE.test(raw)) return fallbackStrategyUnavailable();
  return raw;
}

export function userFacingCurrentResultsError(message) {
  const raw = String(message || '').trim();
  if (!raw) return fallbackCurrentResultsUnavailable();
  if (LEGACY_JARGON_RE.test(raw)) return fallbackCurrentResultsUnavailable();
  return raw;
}
